import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  TemplateRef,
  ViewChild,
  computed,
  forwardRef,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MailIconComponent, KeyIconComponent } from '../icons';

@Component({
  selector: 'app-floating-input',
  standalone: true,
  imports: [CommonModule, FormsModule, MailIconComponent, KeyIconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FloatingInputComponent),
      multi: true,
    },
  ],
  templateUrl: './floating-input.component.html',
  styleUrls: ['./floating-input.component.scss'],
})
export class FloatingInputComponent implements ControlValueAccessor {
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;

  @Input() label = '';
  @Input() placeholder = '';
  @Input() type: 'text' | 'password' | 'email' = 'text';
  @Input() disabled = false;
  @Input() leftIconTemplate?: TemplateRef<any>;
  @Input() rightIconTemplate?: TemplateRef<any>;
  @Input() error = '';

  @Input()
  set value(val: string) {
    this._value.set(val || '');
  }

  get value(): string {
    return this._value();
  }

  // State signals
  protected readonly _value = signal<string>('');
  protected readonly isFocused = signal<boolean>(false);
  protected readonly showPassword = signal<boolean>(false);
  protected readonly _error = signal<string>('');

  protected readonly isFloating = computed(
    () => this.isFocused() || this._value().length > 0
  );

  protected readonly currentInputType = computed(() => {
    if (this.type === 'password') {
      return this.showPassword() ? 'text' : 'password';
    }
    return this.type;
  });

  protected readonly showDefaultLeftIcon = computed(() => {
    return (
      !this.leftIconTemplate &&
      (this.type === 'email' || this.type === 'password')
    );
  });

  protected readonly showDefaultRightIcon = computed(() => {
    return !this.rightIconTemplate && this.type === 'password';
  });

  private onChange: (value: string) => void = () => {};
  private onTouchedFn: () => void = () => {};

  // ControlValueAccessor implementation
  writeValue(value: string | null): void {
    this._value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Event handlers
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this._value.set(value);
    this.onChange(value);
  }

  onFocus(): void {
    this.isFocused.set(true);
  }

  onBlur(): void {
    this.isFocused.set(false);
    this.onTouchedFn();
  }

  onContainerClick(): void {
    if (this.inputElement && !this.disabled) {
      this.inputElement.nativeElement.focus();
    }
  }

  togglePasswordVisibility(event?: Event): void {
    event?.stopPropagation();
    this.showPassword.set(!this.showPassword());
  }

  // Get default icon type based on input type
  getDefaultLeftIconType(): 'mail' | 'key' | null {
    if (this.type === 'email') return 'mail';
    if (this.type === 'password') return 'key';
    return null;
  }
}
